import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ChevronDown } from 'lucide-react';

export default function ClienteDropdown({
  clientes = [],
  selectedValues = [],
  onAdd,
  onRemove,
  isDarkMode = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownContentRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Normalizar clientes: pueden ser strings o objetos {nombre, cuenta}
  const clientesNormalizados = clientes.map(cliente => {
    if (typeof cliente === 'string') {
      return { nombre: cliente, cuenta: '' };
    }
    return cliente;
  });

  // Filtrar por búsqueda
  const clientesFiltrados = searchText.trim()
    ? clientesNormalizados.filter(c => {
        const searchLower = searchText.toLowerCase();
        return c.nombre.toLowerCase().includes(searchLower) || 
               c.cuenta.toLowerCase().includes(searchLower);
      })
    : clientesNormalizados;

  // Cerrar dropdown al clickear fuera
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownContentRef.current && !dropdownContentRef.current.contains(e.target) && 
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calcular posición del dropdown cuando se abre
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const bgClasses = isDarkMode ? 'bg-[#001f16] border-[#00FF66]/25' : 'bg-white border-gray-300';
  const hoverClasses = isDarkMode ? 'hover:border-[#00FF66]/55 hover:bg-[#0a2a1f]' : 'hover:border-emerald-400 hover:bg-emerald-50';

  return (
    <div className="w-full">
      <label className={`${textSecondary} text-xs mb-1 block font-semibold`}>Cliente</label>

      {/* Botón de dropdown */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2.5 rounded-lg border ${bgClasses} ${hoverClasses} flex items-center justify-between text-left transition-all`}
        style={isDarkMode ? { colorScheme: 'dark' } : {}}
      >
        <span className={isDarkMode ? 'text-[#e0ffe8]' : 'text-gray-900'}>
          {selectedValues.length === 0 ? 'Seleccionar...' : `${selectedValues.length} seleccionados`}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: isDarkMode ? '#00FF66' : '#059669' }}
        />
      </button>

      {/* Dropdown en Portal - escapa del contenedor padre */}
      {isOpen && createPortal(
        <div
          ref={dropdownContentRef}
          className={`fixed rounded-lg border ${bgClasses} shadow-2xl z-[9999]`}
          style={{
            top: `${dropdownPos.top}px`,
            left: `${dropdownPos.left}px`,
            width: `${dropdownPos.width}px`,
            minWidth: '300px'
          }}
        >
          {/* Barra de búsqueda */}
          <div className={`flex items-center gap-2 px-3 py-2 border-b ${isDarkMode ? 'border-[#00FF66]/10' : 'border-gray-200'}`}>
            <Search className="w-4 h-4 flex-shrink-0 text-[#00FF66]" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              autoFocus
              className={`flex-1 bg-transparent outline-none text-sm ${
                isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
              }`}
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                className="p-1 hover:bg-[#00FF66]/10 rounded"
              >
                <X className="w-4 h-4 text-[#00FF66]" />
              </button>
            )}
          </div>

          {/* Lista de opciones */}
          <div className="max-h-48 overflow-y-auto">
            {clientesFiltrados.length === 0 ? (
              <div className={`p-3 text-center text-sm ${textSecondary}`}>
                No hay clientes que coincidan
              </div>
            ) : (
              clientesFiltrados.map((cliente) => {
                const isSelected = selectedValues.includes(cliente.nombre);
                return (
                  <button
                    key={`${cliente.nombre}-${cliente.cuenta}`}
                    onClick={() => {
                      if (isSelected) {
                        onRemove(cliente.nombre);
                      } else {
                        onAdd(cliente.nombre);
                      }
                    }}
                    className={`w-full text-left px-3 py-3 border-b ${
                      isDarkMode ? 'border-[#00FF66]/10 hover:bg-[#00FF66]/10' : 'border-gray-100 hover:bg-emerald-50'
                    } transition-colors flex items-start gap-3 ${
                      isSelected
                        ? isDarkMode
                          ? 'bg-[#00FF66]/15'
                          : 'bg-emerald-50'
                        : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      {/* Nombre en grande */}
                      <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {cliente.nombre}
                      </div>
                      {/* Cuenta en pequeño */}
                      {cliente.cuenta && (
                        <div className={`text-xs mt-0.5 ${textSecondary}`}>
                          {cliente.cuenta}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Seleccionados (pills) */}
      <div className="flex flex-wrap gap-2 mt-2 min-h-[28px]">
        {selectedValues.length === 0 ? (
          <span className={`${textSecondary} text-xs`}>Sin selección</span>
        ) : (
          selectedValues.map((value) => (
            <span
              key={value}
              className={isDarkMode
                ? 'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-[#00FF66]/12 text-[#bff5d0] border border-[#00FF66]/35'
                : 'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700 border border-emerald-300'
              }
            >
              {value}
              <button
                type="button"
                onClick={() => onRemove(value)}
                className={isDarkMode ? 'text-[#8be5ac] hover:text-white' : 'text-emerald-700 hover:text-emerald-900'}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
      </div>
    </div>
  );
}
